import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Sparkles, Send, Plus, Loader2, ArrowRight, HelpCircle, Compass } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { Chip } from '@/components/Primitives';
import api from '@/lib/api';
import { priorityChip } from '@/lib/format';
import { getLastScreen, SCREEN_PATHS } from '@/lib/screenTracker';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

// Stored responses can be either the new flexible shape
// {kind, text, mainPriority, nextSteps, suggestedTask, navigate}
// or the legacy shape {grounding, mainPriority, nextSteps, suggestedTask, motivation}.
const normalizeResponse = (data) => {
  if (!data) return null;
  const legacy = !data.text && (data.grounding || data.motivation);
  return {
    kind: data.kind || 'plan',
    text: data.text || data.grounding || '',
    mainPriority: data.mainPriority || '',
    nextSteps: Array.isArray(data.nextSteps) ? data.nextSteps.filter(Boolean) : [],
    suggestedTask: data.suggestedTask && data.suggestedTask.title ? data.suggestedTask : null,
    navigate: data.navigate && SCREEN_PATHS[data.navigate] ? data.navigate : null,
    footnote: legacy ? data.motivation : '',
  };
};

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-cyan">{children}</p>
);

const StructuredResponse = ({ data, onAddTask, adding, onNavigate }) => {
  const { t, tEnum } = useLang();
  const r = normalizeResponse(data);
  if (!r) return null;
  const paragraphs = String(r.text).split(/\n+/).filter((p) => p.trim());
  const isClarify = r.kind === 'clarify';

  return (
    <div className="space-y-3" data-testid={`ai-response-${r.kind}`}>
      {paragraphs.length ? (
        <div
          className={cn(
            'rounded-xl px-3 py-2.5',
            isClarify ? 'border border-brand-violet/25 bg-brand-violet/[0.08]' : 'bg-white/[0.04]'
          )}
        >
          {isClarify ? (
            <div className="mb-1 flex items-center gap-1.5 text-brand-violet">
              <HelpCircle className="h-3.5 w-3.5" />
              <span className="text-[11px] font-semibold uppercase tracking-wider">{t('ai.clarifyLabel')}</span>
            </div>
          ) : null}
          {paragraphs.map((p, i) => (
            <p key={`p-${i}-${p.slice(0, 12)}`} data-testid="ai-response-text" className="text-sm leading-6 text-foreground/85 [&:not(:first-child)]:mt-2">
              {p}
            </p>
          ))}
        </div>
      ) : null}

      {r.mainPriority ? (
        <div>
          <SectionLabel>{t('ai.mainPriority')}</SectionLabel>
          <p data-testid="ai-main-priority" className="mt-1 text-sm font-medium leading-6 text-foreground/90">{r.mainPriority}</p>
        </div>
      ) : null}

      {r.nextSteps.length ? (
        <div>
          <SectionLabel>{t('ai.nextSteps')}</SectionLabel>
          <ol className="mt-1.5 space-y-1.5" data-testid="ai-next-steps">
            {r.nextSteps.map((s, i) => (
              <li key={`step-${i}-${String(s).slice(0, 16)}`} className="flex gap-2.5 text-sm leading-6 text-foreground/85">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-cyan/15 text-[11px] font-semibold text-brand-cyan">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {r.suggestedTask ? (
        <div className="rounded-xl border border-brand-cyan/20 bg-brand-cyan/[0.07] p-3">
          <SectionLabel>{t('ai.suggestedTask')}</SectionLabel>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p data-testid="ai-suggested-task-title" className="truncate text-sm font-medium text-foreground/90">{r.suggestedTask.title}</p>
              <Chip className={cn('mt-1', priorityChip(r.suggestedTask.priority))}>{tEnum(r.suggestedTask.priority)}</Chip>
            </div>
            <button
              type="button"
              data-testid="ai-suggested-task-add-button"
              onClick={() => onAddTask(r.suggestedTask)}
              disabled={adding}
              className="btn-brand flex shrink-0 items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold text-brand-ink"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              </span>
              <span>{t('ai.add')}</span>
            </button>
          </div>
        </div>
      ) : null}

      {r.navigate ? (
        <button
          type="button"
          data-testid={`ai-navigate-${r.navigate}`}
          onClick={() => onNavigate(r.navigate)}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-blue/30 bg-brand-blue/10 px-3 py-1.5 text-xs font-medium text-foreground/85 hover:border-brand-cyan/40 hover:bg-brand-blue/15"
        >
          <Compass className="h-3.5 w-3.5 text-brand-cyan" />
          <span>{t('ai.goTo')} {t(`screens.${r.navigate}`)}</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      ) : null}

      {r.footnote ? (
        <p className="border-t border-white/10 pt-2.5 text-sm italic text-foreground/65">{r.footnote}</p>
      ) : null}
    </div>
  );
};

const bubbleEnter = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

const AssistantBubble = ({ message, onAddTask, adding, onNavigate }) => {
  const { t } = useLang();
  if (message.pending) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground/55" data-testid="ai-thinking">
        <Loader2 className="h-4 w-4 animate-spin text-brand-cyan" /> {t('ai.thinking')}
      </div>
    );
  }
  if (!message.response) return null;
  return (
    <motion.div
      initial={bubbleEnter.initial}
      animate={bubbleEnter.animate}
      transition={{ duration: 0.3 }}
      className="max-w-[92%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.06] p-4"
    >
      <div className="mb-2 flex items-center gap-1.5 text-brand-cyan">
        <Sparkles className="h-3.5 w-3.5" />
        <span className="text-[11px] font-semibold uppercase tracking-wider">{t('ai.assistant')}</span>
      </div>
      <StructuredResponse data={message.response} onAddTask={onAddTask} adding={adding} onNavigate={onNavigate} />
    </motion.div>
  );
};

export default function AIAssistant() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [adding, setAdding] = useState(false);
  const endRef = useRef(null);
  // The section the user came from (recorded by RouteTracker). Read once per visit.
  const originScreen = useRef(getLastScreen());

  const QUICK_PROMPTS = [t('ai.prompt1'), t('ai.prompt2'), t('ai.prompt3'), t('ai.prompt4'), t('ai.prompt5')];

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ai/conversations');
      setMessages(res.data);
    } catch (e) {
      toast.error(t('ai.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput('');
    setSending(true);
    const tempId = `temp-${Date.now()}`;
    setMessages((m) => [...m, { id: tempId, message, response: null, pending: true }]);
    try {
      const res = await api.post('/ai/chat', { message, language: lang, screen: originScreen.current });
      setMessages((m) => m.filter((x) => x.id !== tempId).concat(res.data));
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tempId));
      toast.error(t('ai.respondError'));
    } finally {
      setSending(false);
    }
  };

  // Explicit user action: the assistant never writes data by itself.
  const addTask = async (task) => {
    setAdding(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post(
        '/tasks',
        { title: task.title, priority: task.priority, status: 'To Do', dueDate: today },
        { skipAuthRedirect: true }
      );
      toast.success(t('ai.taskAdded'));
    } catch (e) {
      toast.error(t('ai.taskAddError'));
    } finally {
      setAdding(false);
    }
  };

  const goTo = (screen) => {
    const path = SCREEN_PATHS[screen];
    if (path) navigate(path);
  };

  if (loading) return <FullScreenLoader label={t('ai.loader')} />;

  return (
    <AppLayout>
      <AppHeader title={t('ai.title')} subtitle={t('ai.subtitle')} />

      <div data-testid="ai-chat-thread" className="mt-3 space-y-4 pb-[170px]">
        {messages.length === 0 ? (
          <div className="glass-card rounded-2xl p-5" data-testid="ai-orientation">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-cyan/15 text-brand-cyan">
              <Sparkles className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold text-foreground/90">{t('ai.emptyTitle')}</p>
            <p className="mt-1.5 text-sm leading-6 text-foreground/65">{t('ai.orientation')}</p>
            <p className="mt-3 text-xs text-foreground/45">{t('ai.emptySubtitle')}</p>
          </div>
        ) : null}

        {messages.map((m) => (
          <div key={m.id} className="space-y-3">
            <div className="flex justify-end">
              <div data-testid="ai-user-message" className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand-blue/20 px-3.5 py-2.5 text-sm text-foreground/90">
                {m.message}
              </div>
            </div>
            {m.pending || m.response ? (
              <AssistantBubble message={m} onAddTask={addTask} adding={adding} onNavigate={goTo} />
            ) : null}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="fixed bottom-[72px] left-1/2 z-40 w-full max-w-[480px] -translate-x-1/2 border-t border-white/10 bg-[#07111F]/90 px-4 py-3 backdrop-blur-md">
        {messages.length === 0 ? (
          <div className="no-scrollbar mb-2 flex gap-2 overflow-x-auto" data-testid="ai-quick-prompts">
            {QUICK_PROMPTS.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="shrink-0 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs text-foreground/70 hover:bg-white/[0.1]"
              >
                {q}
              </button>
            ))}
          </div>
        ) : null}
        <div className="flex items-end gap-2">
          <textarea
            data-testid="ai-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder={t('ai.composerPlaceholder')}
            className="max-h-28 min-h-[44px] flex-1 resize-none rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-brand-cyan/40"
          />
          <button
            data-testid="ai-chat-send-button"
            onClick={() => send()}
            disabled={sending || !input.trim()}
            className="btn-brand flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-brand-ink"
          >
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
